import type { FlagVerdictSource, RowProvenance, TenantFlagView } from '@features/feature-flags';

import type { Component } from '../schemas/component';
import type {
    ComponentSettingsView,
    ComponentSettingStatusView,
} from '../schemas/component-settings';

/**
 * One component, as one museum's Components tab has to show it.
 *
 * Out here rather than inside the card component because `apps/web` runs vitest
 * on `node` with no jsdom: a state a component derives for itself is a state no
 * test can read, and this is the module whose mistakes are invisible — every
 * one of them renders *something*, just the wrong thing. It is the same posture
 * `tenant-flag-row.ts` takes for the flags table.
 *
 * ## Three sources, and none of them is enough alone
 *
 * - The **catalog row** says what the component is and which flags it holds.
 * - The **museum's flags** say whether it is on. Entitlement is not stored
 *   anywhere as a boolean; it is read back off the grantors on the museum's
 *   override rows, which is where activation writes it.
 * - The **settings view** says how far the configuration got, and — for a museum
 *   that is *off* — whether it was ever provisioned at all.
 *
 * The settings view is optional because its query is per-component and may not
 * have landed, or may have failed, while the flags have. Absent is treated as
 * "no rows known", never as "no rows exist", which is why it cannot on its own
 * push a card into the provisioned state.
 *
 * ## Why entitlement is recomputed here rather than read from `settings.activated`
 *
 * It is not recomputed to second-guess the API — the two agree, and they read
 * the same rule. It is recomputed because the flags arrive first and for every
 * component at once, while `settings` is one query per card. A tab that waited
 * for N settings queries before it could say which components a museum holds
 * would render N spinners to answer a question it already had the data for.
 */

/**
 * What the card is, in one word. Seven states rather than a pair of booleans:
 * two of them are not "off" in any sense the operator acts on, and two more
 * decline to answer rather than guess at this museum's history.
 */
export type ComponentCardState =
    /**
     * The component holds no flags, so there is nothing to grant and no row
     * anywhere that could carry its grantor. It can **never** read as on, by
     * construction rather than by accident — see the closing note of
     * `get-component-settings.use-case.ts`. Rendered as an ordinary off card it
     * would carry an Activate button that appears to do nothing, which is the
     * one outcome that teaches an operator the screen is broken.
     */
    | 'not-activatable'
    /** Held, and every declared setting reports `done`. */
    | 'on'
    /** Held, and some setting is not `done` yet. {@link ComponentCard.outstanding} counts them. */
    | 'on-outstanding'
    /**
     * Not held, but rows survive from a previous activation. Deactivation takes
     * the grants and keeps the rows precisely so this state can exist: the work
     * is still there and re-enabling is one click, not a re-provisioning.
     */
    | 'off-provisioned'
    /**
     * Not held, and nothing was ever provisioned. Nobody has sold this museum
     * this component — and that claim is only made when the component declares
     * something an activation would have provisioned. See
     * {@link ComponentCardState.off-nothing-declared} for the other half.
     */
    | 'off-never-activated'
    /**
     * Not held, the checklist is known to be empty, and **it proves nothing**:
     * the component declares no configuration, so no activation could ever have
     * created a row for anybody.
     *
     * A seventh state on exactly the reasoning `off-unknown` was added on
     * 2026-09-22 — a claim about this museum's history that is not known to be
     * true does not get made. This one was watched happening in dev on
     * 2026-09-23: a museum read "has never held this component" minutes after
     * an operator deactivated it, because the fallback for "no rows kept" is a
     * sentence about history and the component in question has no rows to keep.
     *
     * Absent `settings` and empty `settings` land here together, deliberately.
     * The API's domain forbids a reader deciding what absent means
     * (`domain/component.ts:255-262`), and both cases leave the card in the
     * same position: unable to prove the sale never happened.
     */
    | 'off-nothing-declared'
    /**
     * Not held, and whether it ever was is **not known yet** — the checklist
     * query has not landed, or it failed.
     *
     * A sixth state rather than a default into one of the two above, because
     * both of those say something about this museum's history and neither is
     * known to be true. Collapsing it into `off-never-activated` is what this
     * tab shipped with on 2026-09-22, and a provisioned museum read "has never
     * held this component" for as long as its own query was in flight. A
     * sentence that is false for half a second is still one an operator can
     * read and act on.
     */
    | 'off-unknown';

/**
 * One member flag, as this museum actually resolves it (`D15` rules 2 and 4).
 *
 * The component is the face and the flag is the truth (`D6`), and the face was
 * showing only whether the component had been sold. `verdict` — *what this
 * museum actually gets, computed server-side* — arrives on every tenant flag
 * (`feature-flags/schemas/tenant-flag.ts:15-19`) and this module used to drop
 * it, so a component could read **on** while a member of it resolved `false`
 * for the client and nothing said so.
 *
 * `known` is a fact about this **read**, not about the museum. A member key the
 * museum's flags response never mentions cannot be given a state, and defaulting
 * it to `off` would report a capability as withheld when the truth is that
 * nothing was asked about it — the same distinction `off-unknown` draws for the
 * card as a whole.
 */
export type ComponentMemberFlag =
    | { readonly key: string; readonly known: false }
    | {
          readonly key: string;
          readonly known: true;
          /** What this museum resolves the flag to. Never the stored toggle. */
          readonly enabled: boolean;
          /** Why it resolves that way — `archived` and `rollout` are the two that surprise people. */
          readonly source: FlagVerdictSource;
          /**
           * What is holding the row up, in the shape the **Set by** column
           * reads. Handed over whole so the card names a grant with that
           * column's own function rather than a second wording of it.
           */
          readonly provenance: RowProvenance;
      };

export interface ComponentCard {
    readonly component: Component;
    readonly state: ComponentCardState;
    /**
     * Every member flag the component declares, in the order an operator
     * arranged them — editorial order, kept for the reason the schema gives
     * (`schemas/component.ts:26-33`).
     *
     * Always present, on an off card as much as an on one: a member still
     * resolving `true` after the component is deactivated is precisely the
     * disagreement `D5` asks this surface to stop hiding.
     */
    readonly members: readonly ComponentMemberFlag[];
    /**
     * The checklist, verbatim — unknown statuses included, uncoerced. A status
     * this build has no copy for is still this museum's true state, and
     * rewriting it to `not_started` would tell an operator that work nobody has
     * looked at is work nobody has started.
     *
     * Empty when the settings view has not arrived, which is not the same fact
     * as an empty checklist and is why {@link state} never reads emptiness here
     * as evidence of anything.
     */
    readonly settings: readonly ComponentSettingStatusView[];
    /**
     * How many settings are not `done`. Zero whenever the card is off, because
     * there is nothing outstanding about work on a component nobody holds.
     */
    readonly outstanding: number;
}

export interface ComponentCardInput {
    readonly component: Component;
    /** The whole tenant flags read. Filtered here against the component's members. */
    readonly flags: readonly TenantFlagView[];
    /** Absent while its query is in flight, or after one that failed. */
    readonly settings?: ComponentSettingsView | undefined;
}

/**
 * The one status that means a setting is finished. Compared against rather than
 * enumerated, for the reason the schema gives: the backend grows this set on its
 * own release train, and everything that is not `done` is outstanding — which
 * stays true of a value invented after this build shipped.
 */
const DONE = 'done';

export function componentCard(input: ComponentCardInput): ComponentCard {
    const { component, flags, settings } = input;
    const rows = settings?.settings ?? [];
    const members = memberStates(component, flags);

    if (component.flagKeys.length === 0) {
        return { component, state: 'not-activatable', members, settings: rows, outstanding: 0 };
    }

    if (!isHeld(component, flags)) {
        return {
            component,
            // Four outcomes, not two. The checklist is unknown; or it is known
            // and has rows; or it is known and empty *and the component
            // declares something that would have produced rows*, which makes
            // emptiness proof; or it is known and empty and the component
            // declares nothing, which makes emptiness proof of nothing at all.
            // Only the middle two are claims about this museum's history that
            // can be made out loud.
            state:
                settings === undefined
                    ? 'off-unknown'
                    : rows.length > 0
                      ? 'off-provisioned'
                      : declaresConfiguration(component)
                        ? 'off-never-activated'
                        : 'off-nothing-declared',
            members,
            settings: rows,
            outstanding: 0,
        };
    }

    const outstanding = rows.filter((row) => row.status !== DONE).length;

    return {
        component,
        state: outstanding > 0 ? 'on-outstanding' : 'on',
        members,
        settings: rows,
        outstanding,
    };
}

/**
 * Whether an activation of this component would have left a trace.
 *
 * The question the never-held sentence actually rests on, asked out loud
 * instead of inferred from an empty list. A component that declares knobs seeds
 * a checklist row per knob when it is activated and keeps them through
 * deactivation, so no rows really does mean no activation ever happened. A
 * component that declares none has nothing to seed, and its checklist is empty
 * for every museum on earth — including the one an operator activated a minute
 * ago.
 *
 * Absent and empty are answered the same, and not by accident: the API's own
 * domain says a reader that had to tell them apart would be deciding what
 * absent means (`domain/component.ts:255-262`).
 */
function declaresConfiguration(component: Component): boolean {
    return (component.settings ?? []).length > 0;
}

/**
 * Each declared member, matched against the museum's flags read.
 *
 * Driven by `component.flagKeys` rather than by the flags array, because the
 * component's list is the question being asked: a member the response never
 * carried is a fact worth rendering, and iterating the response instead would
 * silently drop it.
 */
function memberStates(
    component: Component,
    flags: readonly TenantFlagView[],
): readonly ComponentMemberFlag[] {
    const byKey = new Map(flags.map((flag) => [flag.definition.key, flag]));

    return component.flagKeys.map((key) => {
        const flag = byKey.get(key);

        if (flag === undefined) return { key, known: false };

        return {
            key,
            known: true,
            enabled: flag.verdict.enabled,
            source: flag.verdict.source,
            provenance: {
                // Never `grants.length`: an override written before provenance
                // existed carries no grantors at all, and reading that as
                // "inherited" would hide a row that is in fact pinning this
                // museum. The column draws the same line at `:76-83`.
                origin: flag.override === null ? 'default' : 'override',
                grants: flag.override?.grantedBy ?? [],
            },
        };
    });
}

/**
 * Whether this museum holds this component.
 *
 * Both halves of the test are load-bearing. The flag must be one the component
 * holds — a grant on some unrelated flag says nothing — and the grant must name
 * **this component's id**, not merely be of kind `component`: a museum granted
 * the same flag by another component, by a product, or by an operator's hand was
 * not sold this one. This mirrors `grantsComponent` in the API's
 * `get-component-settings.use-case.ts`, deliberately, because the two answers
 * disagreeing would show a card as on that the API will not give a checklist to.
 *
 * A row with no `grantedBy` predates provenance and grants nothing here: it is
 * an override somebody set by hand before components existed, and reading it as
 * a component grant would claim a sale that never happened.
 */
function isHeld(component: Component, flags: readonly TenantFlagView[]): boolean {
    const members = new Set(component.flagKeys);

    return flags.some(
        (flag) =>
            members.has(flag.definition.key) &&
            (flag.override?.grantedBy ?? []).some(
                (grant) => grant.kind === 'component' && grant.id === component.id,
            ),
    );
}
